import React, { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DatePicker } from 'antd';
import { SaveOutlined, UnorderedListOutlined } from '@ant-design/icons';

import BorderlessButton from '../../../common/buttons/BorderlessButton';
import PencilIcon from '../../../common/icons/files/PencilIcon';
import TrashIcon from '../../../common/icons/files/TrashIcon';
import MaterialUnitsSelector from '../../../common/inputs/MaterialUnitsSelector';
import VerticallyCenteredNumberInput from '../../../common/inputs/VerticallyCenteredNumberInput';
import CustomConfirmModal from '../../../common/modals/CustomConfirmModal';
import TagRender from '../../../common/TagRender';

import { EQUIPMENT_CATEGORY_ID, MATERIAL_CATEGORY_ID } from '../../../helpers/costcodeHelpers';
import { isNullOrUndefined } from '../../../helpers/helpers';
import { currencyFormatter, currencyParser } from '../../../helpers/inputParsers';

import {
  buildCostcodesTableData,
  calculateEquipmentCost,
  calculateEstimatedCostFromHours,
  getCostcodeKey,
  getRowsForCostcode,
  mergeEditingIntoDataSource,
} from '../budgetTrackingHelpers';
import { showCostUpdatesIcon } from '../../ProjectProgress/progressHelpers';
import CostCodeSelectionTable from './CostCodeSelectionTable';
import CostTrackingCostUpdatesModal from './manage/CostTrackingCostUpdatesModal';
import OntraccrTag from '../../../common/OntraccrTag';
import Colors from '../../../constants/Colors';
import { buildColumns, getColumnOptions } from './manage/costTrackingHelpers';

export const COST_CODE_COLUMNS = [
  { key: 'name', title: 'Name', dataIndex: 'name' },
  { key: 'code', title: 'Code', dataIndex: 'code' },
  {
    key: 'description', title: 'Description', dataIndex: 'description', width: 200,
  },
  { key: 'category', title: 'Category', dataIndex: 'categoryId' },
  {
    key: 'estimatedCost', title: 'Total', dataIndex: 'estimatedCost', width: 120,
  },
  {
    key: 'hours', title: 'Hours', dataIndex: 'hours', width: 100,
  },
  {
    key: 'days', title: 'Days', dataIndex: 'days', width: 100,
  },
  {
    key: 'dailyWage', title: 'Daily Cost', dataIndex: 'dailyWage', width: 120,
  },
  {
    key: 'hourlyWage', title: 'Hourly Cost', dataIndex: 'hourlyWage', width: 120,
  },
  {
    key: 'estimatedQuantity', title: 'Est. Quantity', dataIndex: 'estimatedQuantity', width: 120,
  },
  {
    key: 'units', title: 'Units', dataIndex: 'units', width: 120,
  },
  {
    key: 'dates', title: 'Dates', dataIndex: 'dates', width: 300, showIf: ({ dates }) => dates,
  },
  {
    key: 'costUpdates', title: 'Updates', dataIndex: 'costUpdates', width: 60,
  },
  {
    key: 'edit',
    title: '',
    label: 'Edit',
    dataIndex: 'edit',
    width: 60,
    showIf: ({ showEdit }) => showEdit,
  },
  {
    key: 'delete',
    title: '',
    label: 'Delete',
    dataIndex: 'delete',
    width: 60,
    showIf: ({ showDelete }) => showDelete,
  },
];

export const getCostCodeColumnOptions = (flags) => getColumnOptions(COST_CODE_COLUMNS, flags);

const formatDates = ({ startDate, endDate }) => {
  if (!startDate || !endDate) return '';
  return `${moment(startDate).format('YYYY-MM-DD')} -> ${moment(endDate).format('YYYY-MM-DD')}`;
};

const INPUT_OPTIONS = {
  formatter: currencyFormatter,
  parser: currencyParser,
};

export default function BudgetTrackingCostCodesTable({
  unphasedCostcodes,
  phases,
  expandedData,
  isNotDisplay,
  onEditCostcode,
  onEditPhaseCostCodes,
  onRemoveCostcode,
  showEditButton,
  useBuildEstimate,
  selectedRowKeys,
  onSelectionChange,
  emptyText,
  scrollY,
  dates,
  hiddenColumnKeys,
}) {
  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);

  // Parent/children for multi-category (unphased and phased)
  const expandedDataSource = useMemo(() => {
    if (expandedData) return expandedData;
    const unphasedData = buildCostcodesTableData(unphasedCostcodes);
    if (phases && phases.length > 0) {
      const phasedData = phases.flatMap((phase) => buildCostcodesTableData(
        phase.costcodes || [],
        { phaseId: phase.id, phaseName: phase.name },
      ));
      return [...unphasedData, ...phasedData];
    }
    return unphasedData;
  }, [unphasedCostcodes, phases, expandedData]);

  const [editingCostcodeKey, setEditingCostcodeKey] = useState(null);
  const [editingCostcodeData, setEditingCostcodeData] = useState({});
  const [costUpdatesRecord, setCostUpdatesRecord] = useState(null);

  const closeCostUpdatesModal = useCallback(() => setCostUpdatesRecord(null), []);

  const effectiveDataSource = useMemo(() => {
    if (!editingCostcodeKey || Object.keys(editingCostcodeData).length === 0) {
      return expandedDataSource;
    }
    return mergeEditingIntoDataSource(expandedDataSource, editingCostcodeData, editingCostcodeKey);
  }, [expandedDataSource, editingCostcodeKey, editingCostcodeData]);

  const applyUpdateToRecord = useCallback((rec, updateObj) => {
    const newRec = { ...rec };
    newRec.categoryAssignments = { ...rec.categoryAssignments };
    Object.entries(updateObj.categoryAssignments || {}).forEach(([catId, data]) => {
      newRec.categoryAssignments[catId] = { ...newRec.categoryAssignments[catId], ...data };
    });
    // Table rows use categoryAssignment (singular) for display; keep it in sync
    if (rec.categoryId != null && updateObj.categoryAssignments?.[rec.categoryId]) {
      newRec.categoryAssignment = {
        ...newRec.categoryAssignment,
        ...updateObj.categoryAssignments[rec.categoryId],
      };
    }
    Object.keys(updateObj).filter((k) => k !== 'categoryAssignments').forEach((k) => {
      newRec[k] = updateObj[k];
    });
    return newRec;
  }, []);

  const onValueChanged = useCallback(({
    record,
    key,
  }) => (value) => {
    let valueToUse = value;

    if (Number.isNaN(value)) {
      valueToUse = 0;
    }

    const { categoryId, phaseId } = record;
    const costcodeKey = getCostcodeKey(record);

    const updateObj = {
      categoryAssignments: {
        [categoryId]: {},
      },
    };

    const costcodeCategoryKeys = new Set(['hours', 'days', 'estimatedCost', 'estimatedQuantity', 'units']);

    if (costcodeCategoryKeys.has(key)) {
      updateObj.categoryAssignments[categoryId] = {
        [key]: valueToUse,
      };
    } else {
      updateObj[key] = valueToUse;
    }

    const isNotEquipment = categoryId !== EQUIPMENT_CATEGORY_ID;

    if (useBuildEstimate && key === 'hours' && isNotEquipment) {
      const { hourlyWage = 0 } = record;
      const calculatedEstimatedCost = calculateEstimatedCostFromHours(
        hourlyWage,
        valueToUse,
      ) || 0;
      updateObj.categoryAssignments[categoryId].estimatedCost = calculatedEstimatedCost;
    }

    if (useBuildEstimate && (key === 'dailyWage' || key === 'hourlyWage' || key === 'hours' || key === 'days') && !isNotEquipment) {
      const updatedCategoryAssignments = {
        ...record.categoryAssignments,
        [categoryId]: {
          ...record.categoryAssignments?.[categoryId],
          ...updateObj.categoryAssignments[categoryId],
        },
      };
      const updatedRecord = {
        ...record,
        [key]: valueToUse,
        categoryAssignments: updatedCategoryAssignments,
      };
      const calculatedEstimatedCost = calculateEquipmentCost(
        updatedRecord,
        EQUIPMENT_CATEGORY_ID,
      );
      updateObj.categoryAssignments[categoryId].estimatedCost = calculatedEstimatedCost;
    }

    // When editing locally, update state instead of calling parent
    if (editingCostcodeKey && costcodeKey === editingCostcodeKey) {
      const { rowKey } = record;
      setEditingCostcodeData((prev) => {
        const current = prev[rowKey] ?? record;
        const updated = applyUpdateToRecord(current, updateObj);
        return { ...prev, [rowKey]: updated };
      });
      return;
    }

    // Handle phased vs unphased costcodes
    if (isNullOrUndefined(phaseId)) {
      // Unphased costcode
      onEditCostcode(record, updateObj);
    } else {
      // Find the phase and update the costcode within it
      const phase = phases.find((p) => p.id === phaseId);
      if (!phase) return;
      const updatedPhaseCostcodes = phase?.costcodes?.map((cc) => {
        if (cc.id !== record.id) return cc;

        // Merge the update into the costcode
        const newCategoryAssignments = {
          ...cc.categoryAssignments,
        };

        // Merge category assignment updates
        Object.entries(updateObj.categoryAssignments || {}).forEach(([catId, catData]) => {
          newCategoryAssignments[catId] = {
            ...newCategoryAssignments[catId],
            ...catData,
          };
        });

        return {
          ...cc,
          ...updateObj,
          categoryAssignments: newCategoryAssignments,
        };
      }) ?? [];
      onEditPhaseCostCodes(
        phaseId,
        updatedPhaseCostcodes,
        record.id, // used in CostTrackingBudget.jsx to find the edited costcode
      );
    }
  }, [
    onEditCostcode,
    onEditPhaseCostCodes,
    phases,
    useBuildEstimate,
    editingCostcodeKey,
    applyUpdateToRecord,
  ]);

  const handleDateChange = useCallback((record, { startDate, endDate }) => {
    const updateObj = { startDate: startDate ?? null, endDate: endDate ?? null };
    const costcodeKey = getCostcodeKey(record);

    // When editing locally, update state instead of calling parent (same as onValueChanged)
    if (editingCostcodeKey && costcodeKey === editingCostcodeKey) {
      const { rowKey } = record;
      setEditingCostcodeData((prev) => {
        const current = prev[rowKey] ?? record;
        return { ...prev, [rowKey]: { ...current, ...updateObj } };
      });
      return;
    }

    const { phaseId } = record;
    if (isNullOrUndefined(phaseId)) {
      onEditCostcode(record, updateObj);
    } else {
      const phase = phases.find((p) => p.id === phaseId);
      if (!phase) return;
      const { id: costcodeId } = record;
      const updatedPhaseCostcodes = (phase.costcodes || []).map((cc) => (
        cc.id === costcodeId ? { ...cc, ...updateObj } : cc
      ));
      onEditPhaseCostCodes(
        phaseId,
        updatedPhaseCostcodes,
        costcodeId, // used in CostTrackingBudget.jsx to find the edited costcode
      );
    }
  }, [editingCostcodeKey, onEditCostcode, onEditPhaseCostCodes, phases]);

  const handleRemoveCostcode = useCallback((record) => {
    const costcodeToRemove = {
      name: record.name,
      id: record.id,
    };

    CustomConfirmModal({
      title: 'Delete Cost Code',
      content: 'Deleting this cost code will remove all category estimates associated with it. Are you sure you want to continue?',
      okText: 'Delete',
      cancelText: 'Cancel',
      onOk: () => onRemoveCostcode(costcodeToRemove),
    });
  }, [onRemoveCostcode]);

  const handleStartEdit = useCallback((record) => {
    const key = getCostcodeKey(record);
    const rows = getRowsForCostcode(expandedDataSource, key);
    const data = rows.reduce((acc, r) => ({
      ...acc,
      [r.rowKey]: { ...r, categoryAssignment: { ...r.categoryAssignment } },
    }), {});
    setEditingCostcodeKey(key);
    setEditingCostcodeData(data);
  }, [expandedDataSource]);

  const handleSaveEdit = useCallback(async () => {
    const rows = Object.values(editingCostcodeData);
    if (rows.length === 0) return;
    const primaryRecord = rows.find((r) => r.isParentRow) || rows[0];
    const { phaseId } = primaryRecord;

    const updateObj = {
      categoryAssignments: {},
      startDate: primaryRecord.startDate ?? null,
      endDate: primaryRecord.endDate ?? null,
    };
    rows.forEach((rec) => {
      if (rec.categoryId != null) {
        updateObj.categoryAssignments[rec.categoryId] = rec.categoryAssignment || {};
      }
    });

    if (isNullOrUndefined(phaseId)) {
      await onEditCostcode(primaryRecord, updateObj);
    } else {
      const phase = phases.find((p) => p.id === phaseId);
      if (!phase) return;
      const { id: costcodeId } = primaryRecord;
      const categoryIds = [...new Set(rows.map((r) => r.categoryId).filter(Boolean))];
      const categoryAssignments = rows.reduce((acc, r) => {
        if (r.categoryId != null) acc[r.categoryId] = r.categoryAssignment || {};
        return acc;
      }, {});
      const mergedCostcode = {
        ...primaryRecord,
        id: costcodeId,
        categoryIds,
        categoryAssignments,
        startDate: updateObj.startDate,
        endDate: updateObj.endDate,
      };
      const updatedPhaseCostcodes = (phase.costcodes || []).map((cc) => (
        cc.id === costcodeId ? mergedCostcode : cc
      ));
      await onEditPhaseCostCodes(
        phaseId,
        updatedPhaseCostcodes,
        costcodeId, // used in CostTrackingBudget.jsx to find the edited costcode
      );
    }

    setEditingCostcodeKey(null);
    setEditingCostcodeData({});
  }, [editingCostcodeData, onEditCostcode, onEditPhaseCostCodes, phases]);

  const columns = useMemo(() => {
    const isEditingRow = (record) => (
      editingCostcodeKey && getCostcodeKey(record) === editingCostcodeKey
    );

    const showInput = (record) => (
      isNotDisplay || isEditingRow(record)
    );

    const columnProps = {
      name: {
        render: (val, record) => (record.isChildRow ? '' : (val ?? '-')),
      },
      code: {
        render: (val, record) => (record.isChildRow ? '' : (val ?? '-')),
      },
      description: {
        render: (val, record) => (record.isChildRow ? '' : (val ?? '-')),
      },
      category: {
        render: (_, record) => {
          const { categoryId, isParentRow } = record;
          if (isParentRow) {
            return (
              <OntraccrTag style={{ fontSize: 12, fontFamily: 'open-sans-600', color: Colors.GRAY_950 }}>
                Multi
              </OntraccrTag>
            );
          }
          if (!categoryId) return '-';
          return (
            <TagRender
              tagMap={categoriesIdMap}
              tagIds={[categoryId]}
              useRedesign
            />
          );
        },
      },
      estimatedCost: {
        render: (_, record) => {
          const { categoryAssignment = {}, isParentRow } = record;
          const { estimatedCost } = categoryAssignment;

          if (!showInput(record)) {
            return estimatedCost ? INPUT_OPTIONS.formatter(estimatedCost) : '-';
          }
          return (
            <VerticallyCenteredNumberInput
              min={0}
              value={estimatedCost}
              onChange={onValueChanged({ record, key: 'estimatedCost' })}
              style={{ width: 100 }}
              formatter={INPUT_OPTIONS.formatter}
              parser={INPUT_OPTIONS.parser}
              asFloat
              className="budget-tracking-display-dropdown"
              disabled={isParentRow}
            />
          );
        },
      },
      hours: {
        render: (_, record) => {
          const { categoryAssignment = {}, categoryId, isParentRow } = record;
          const { hours } = categoryAssignment;
          const isMaterial = categoryId === MATERIAL_CATEGORY_ID;

          if (!showInput(record)) return hours ?? '-';
          return (
            <VerticallyCenteredNumberInput
              min={0}
              value={hours}
              onChange={onValueChanged({ record, key: 'hours' })}
              style={{ width: 80 }}
              asFloat
              className="budget-tracking-display-dropdown"
              disabled={isParentRow || isMaterial}
            />
          );
        },
      },
      days: {
        render: (_, record) => {
          const { categoryAssignment = {}, categoryId, isParentRow } = record;
          const { days } = categoryAssignment;
          const isEquipment = categoryId === EQUIPMENT_CATEGORY_ID;

          if (!showInput(record)) return days ?? '-';
          return (
            <VerticallyCenteredNumberInput
              min={0}
              value={days}
              onChange={onValueChanged({ record, key: 'days' })}
              style={{ width: 80 }}
              asFloat
              className="budget-tracking-display-dropdown"
              disabled={isParentRow || !isEquipment}
            />
          );
        },
      },
      dailyWage: {
        render: (dailyWage, record) => {
          const { categoryId, isParentRow } = record;
          const isEquipment = categoryId === EQUIPMENT_CATEGORY_ID;

          if (!showInput(record)) {
            return dailyWage ? INPUT_OPTIONS.formatter(dailyWage) : '-';
          }
          return (
            <VerticallyCenteredNumberInput
              min={0}
              value={dailyWage}
              onChange={onValueChanged({ record, key: 'dailyWage' })}
              style={{ width: 100 }}
              formatter={INPUT_OPTIONS.formatter}
              parser={INPUT_OPTIONS.parser}
              asFloat
              className="budget-tracking-display-dropdown"
              disabled={isParentRow || !isEquipment}
            />
          );
        },
      },
      hourlyWage: {
        render: (hourlyWage, record) => {
          const { categoryId, isParentRow } = record;
          const isEquipment = categoryId === EQUIPMENT_CATEGORY_ID;

          if (!showInput(record)) {
            return hourlyWage ? INPUT_OPTIONS.formatter(hourlyWage) : '-';
          }
          return (
            <VerticallyCenteredNumberInput
              min={0}
              value={hourlyWage}
              onChange={onValueChanged({ record, key: 'hourlyWage' })}
              style={{ width: 100 }}
              formatter={INPUT_OPTIONS.formatter}
              parser={INPUT_OPTIONS.parser}
              asFloat
              className="budget-tracking-display-dropdown"
              disabled={isParentRow || !isEquipment}
            />
          );
        },
      },
      estimatedQuantity: {
        render: (_, record) => {
          const { categoryAssignment = {}, categoryId, isParentRow } = record;
          const { estimatedQuantity } = categoryAssignment;
          const isMaterial = categoryId === MATERIAL_CATEGORY_ID;

          if (!showInput(record)) return estimatedQuantity ?? '-';
          return (
            <VerticallyCenteredNumberInput
              min={0}
              value={estimatedQuantity}
              onChange={onValueChanged({ record, key: 'estimatedQuantity' })}
              style={{ width: 100 }}
              asFloat
              className="budget-tracking-display-dropdown"
              disabled={isParentRow || !isMaterial}
            />
          );
        },
      },
      units: {
        render: (_, record) => {
          const { categoryAssignment = {}, categoryId, isParentRow } = record;
          const { units, estimatedQuantity } = categoryAssignment;
          const isMaterial = categoryId === MATERIAL_CATEGORY_ID;

          if (!showInput(record)) return units ?? '-';
          return (
            <MaterialUnitsSelector
              value={units}
              onSelect={onValueChanged({ record, key: 'units' })}
              className="budget-tracking-display-dropdown"
              disabled={isParentRow || !isMaterial || !estimatedQuantity}
              hidePlaceholder={isParentRow}
            />
          );
        },
      },
      dates: {
        render: (_, record) => {
          if (handleDateChange) {
            const { isParentRow } = record;
            return (
              <DatePicker.RangePicker
                style={{ width: 300 }}
                className="form-field-datetime-container"
                onChange={([start, end] = []) => handleDateChange(record, {
                  startDate: start ?? null,
                  endDate: end ?? null,
                })}
                value={
                  record.startDate && record.endDate
                    ? [moment(record.startDate), moment(record.endDate)]
                    : null
                }
                disabled={isParentRow}
              />
            );
          }
          return formatDates(record);
        },
      },
      costUpdates: {
        render: (_, record) => (
          showCostUpdatesIcon(record) ? (
            <BorderlessButton
              iconNode={<UnorderedListOutlined style={{ fontSize: 16, color: Colors.GRAY_800 }} />}
              onClick={(e) => {
                e.stopPropagation();
                setCostUpdatesRecord(record);
              }}
            />
          ) : '-'
        ),
      },
      edit: {
        render: (_, record) => {
          if (record.isChildRow) return null;
          const costcodeKey = getCostcodeKey(record);
          const isEditing = editingCostcodeKey === costcodeKey;
          const isOtherBeingEdited = editingCostcodeKey != null && !isEditing;
          return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {isEditing ? (
                <BorderlessButton
                  title=""
                  iconNode={<SaveOutlined style={{ fontSize: 16 }} />}
                  onClick={handleSaveEdit}
                  style={{ minWidth: 32, height: 28 }}
                />
              ) : (
                <BorderlessButton
                  title=""
                  iconNode={<PencilIcon width={16} height={16} color={Colors.GRAY_800} />}
                  onClick={isOtherBeingEdited ? undefined : () => handleStartEdit(record)}
                  disabled={isOtherBeingEdited}
                  color={Colors.GRAY_800}
                  style={{
                    opacity: isOtherBeingEdited ? 0.4 : 1,
                    cursor: isOtherBeingEdited ? 'not-allowed' : 'pointer',
                  }}
                />
              )}
            </div>
          );
        },
      },
      delete: {
        render: (_, record) => {
          const { isChildRow } = record;
          if (isChildRow) return null;

          const disabled = !!editingCostcodeKey;

          return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <BorderlessButton
                title=""
                iconNode={<TrashIcon color={Colors.RED_600} width={16} height={16} />}
                onClick={disabled ? undefined : () => handleRemoveCostcode(record)}
                disabled={disabled}
                color={Colors.RED_600}
                style={{ opacity: disabled ? 0.3 : 1 }}
              />
            </div>
          );
        },
      },
    };

    return buildColumns({
      columnDefinitions: COST_CODE_COLUMNS,
      flags: { dates, showEdit: showEditButton, showDelete: !!onRemoveCostcode },
      columnProps,
      hiddenKeySet: hiddenColumnKeys,
    });
  }, [
    categoriesIdMap,
    isNotDisplay,
    onValueChanged,
    showEditButton,
    onRemoveCostcode,
    handleRemoveCostcode,
    editingCostcodeKey,
    handleStartEdit,
    handleSaveEdit,
    dates,
    handleDateChange,
    hiddenColumnKeys,
  ]);

  const expandable = useMemo(() => ({
    rowExpandable: (record) => !!(record.children && record.children.length > 0),
  }), []);

  return (
    <>
      <CostCodeSelectionTable
        columns={columns}
        dataSource={effectiveDataSource}
        rowKey="rowKey"
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={onSelectionChange}
        expandable={expandable}
        emptyText={emptyText || 'Add cost codes to start tracking your budget'}
        scrollY={scrollY}
      />
      <CostTrackingCostUpdatesModal
        visible={!!costUpdatesRecord}
        record={costUpdatesRecord}
        costUpdates={costUpdatesRecord?.costUpdates ?? []}
        onClose={closeCostUpdatesModal}
      />
    </>
  );
}

/* eslint-disable react/forbid-prop-types */
BudgetTrackingCostCodesTable.propTypes = {
  unphasedCostcodes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    code: PropTypes.string,
    description: PropTypes.string,
    categoryIds: PropTypes.arrayOf(PropTypes.string),
  })),
  phases: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    costcodes: PropTypes.array,
  })),
  expandedData: PropTypes.array,
  isNotDisplay: PropTypes.bool,
  onEditCostcode: PropTypes.func,
  onEditPhaseCostCodes: PropTypes.func,
  onRemoveCostcode: PropTypes.func,
  useBuildEstimate: PropTypes.bool,
  selectedRowKeys: PropTypes.arrayOf(PropTypes.string),
  onSelectionChange: PropTypes.func,
  emptyText: PropTypes.string,
  scrollY: PropTypes.number,
  showEditButton: PropTypes.bool,
  dates: PropTypes.bool,
  hiddenColumnKeys: PropTypes.instanceOf(Set),
};

BudgetTrackingCostCodesTable.defaultProps = {
  unphasedCostcodes: [],
  phases: [],
  expandedData: null,
  isNotDisplay: false,
  onEditCostcode: () => {},
  onEditPhaseCostCodes: null,
  onRemoveCostcode: null,
  useBuildEstimate: false,
  selectedRowKeys: [],
  onSelectionChange: null,
  emptyText: null,
  scrollY: undefined,
  showEditButton: false,
  dates: false,
  hiddenColumnKeys: undefined,
};
